import {
  defineRule, configure,
} from 'vee-validate'
import {
  required,
  email,
  numeric,
  max,
  min,
  url,
  mimes,
  image,
} from '@vee-validate/rules'
import {
  localize, setLocale,
} from '@vee-validate/i18n'
import deBase from '@vee-validate/i18n/dist/locale/de.json'
import enBase from '@vee-validate/i18n/dist/locale/en.json'
import frBase from '@vee-validate/i18n/dist/locale/fr.json'
import itBase from '@vee-validate/i18n/dist/locale/it.json'

const customMessages = {
  de: {
    messages: {
      min_files: 'Mindestens {min} Datei(en) sind erforderlich.',
      max_files: 'Es sind maximal {max} Datei(en) zugelassen.',
    },
  },
  en: {
    messages: {
      min_files: 'At least {min} file(s) are required.',
      max_files: 'A maximum of {max} file(s) is allowed.',
    },
  },
  fr: {
    messages: {
      min_files: 'Au moins {min} fichier(s) sont requis.',
      max_files: 'Un maximum de {max} fichier(s) est autorisé.',
    },
  },
  it: {
    messages: {
      min_files: 'Sono richiesti almeno {min} file.',
      max_files: 'È consentito un massimo di {max} file.',
    },
  },
}

const mergedLocales = {
  de: {
    ...deBase,
    ...customMessages.de,
    messages: {
      ...deBase.messages,
      ...customMessages.de.messages,
    },
  },
  en: {
    ...enBase,
    ...customMessages.en,
    messages: {
      ...enBase.messages,
      ...customMessages.en.messages,
    },
  },
  fr: {
    ...frBase,
    ...customMessages.fr,
    messages: {
      ...frBase.messages,
      ...customMessages.fr.messages,
    },
  },
  it: {
    ...itBase,
    ...customMessages.it,
    messages: {
      ...itBase.messages,
      ...customMessages.it.messages,
    },
  },
}

export default defineNuxtPlugin(({ $i18n }) => {
  defineRule('required', required)
  defineRule('email', email)
  defineRule('numeric', numeric)
  defineRule('integer', numeric)
  defineRule('max', max)
  defineRule('min', min)
  defineRule('url', url)
  defineRule('mimes', mimes)
  defineRule('image', image)

  defineRule('phone', (value) => {
    // Field is empty, should pass
    if (!value || !value.length) {
      return true
    }
    const regex = /^(\+|00)[1-9][0-9 \-\(\)\.]{7,32}$/
    return regex.test(value)
  })

  // to handle the nullable fields rules from statamic
  defineRule('nullable', () => {
    return true
  })

  defineRule('max_files', (value, [max]) => {
    if (!value) {
      return true // if no file is selected, it's valid
    }

    if (value.length > max) {
      return `Es sind maximal ${max} Datei(en) zugelassen`
    }

    return true
  })

  defineRule('min_files', (value, [min]) => {
    if (!value) {
      return true // if no file is selected, it's valid
    }

    if (value.length < min) {
      return `Mindestend ${min} Datei(en) sind erforderlich`
    }

    return true
  })

  configure({
    // controls if `blur` events should trigger validation with `handleChange` handler
    validateOnBlur: true,
    // controls if `change` events should trigger validation with `handleChange` handler
    validateOnChange: true,
    // controls if `input` events should trigger validation with `handleChange` handler
    validateOnInput: true,
    // controls if `update:modelValue` events should trigger validation with `handleChange` handler
    validateOnModelUpdate: true,

    generateMessage: localize({
      aplus_de: mergedLocales.de,
      aplus_en: mergedLocales.en,
      aplus_fr: mergedLocales.fr,
      aplus_it: mergedLocales.it,
      ageingsociety_de: mergedLocales.de,
      ageingsociety_en: mergedLocales.en,
      ageingsociety_fr: mergedLocales.fr,
      ageingsociety_it: mergedLocales.it,
      ord_de: mergedLocales.de,
      ord_en: mergedLocales.en,
      ord_fr: mergedLocales.fr,
      ord_it: mergedLocales.it,
      polarresearch_de: mergedLocales.de,
      polarresearch_en: mergedLocales.en,
      polarresearch_fr: mergedLocales.fr,
      polarresearch_it: mergedLocales.it,
      prixmedia_de: mergedLocales.de,
      prixmedia_en: mergedLocales.en,
      prixmedia_fr: mergedLocales.fr,
      prixmedia_it: mergedLocales.it,
      tdnet_de: mergedLocales.de,
      tdnet_en: mergedLocales.en,
      tdnet_fr: mergedLocales.fr,
      tdnet_it: mergedLocales.it,
      youngacademy_de: mergedLocales.de,
      youngacademy_en: mergedLocales.en,
      youngacademy_fr: mergedLocales.fr,
      youngacademy_it: mergedLocales.it,
    }),
  })

  watch($i18n.locale, (newLocale) => {
    setLocale(newLocale)
  })
  setLocale($i18n.locale.value)
})
